import { useState, useEffect, useMemo } from "react";
import { Eye, EyeOff, ZoomIn, ZoomOut } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { useStepScreenshot } from "@/hooks/use-step-screenshot";
import { cn } from "@/lib/utils";
import {
  isValidBox,
  resolveBoxViewport,
  resolveHighlightStyles,
} from "../screenshot-types";
import type { ZenCarouselCanvasProps } from "./zen-types";

export function ZenCarouselCanvas({
  finding,
  showHighlights,
  onToggleHighlights,
  onSelectHighlight,
  focusMode: controlledFocusMode,
  onToggleFocusMode,
  className,
}: ZenCarouselCanvasProps) {
  const { url, loading } = useStepScreenshot(
    finding.walkthroughId,
    finding.stepNumber,
  );
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [internalFocusMode, setInternalFocusMode] = useState<"page" | "target">(
    "page",
  );

  const focusMode = controlledFocusMode ?? internalFocusMode;
  const toggleFocusMode =
    onToggleFocusMode ??
    (() => {
      setInternalFocusMode((curr) => (curr === "target" ? "page" : "target"));
    });

  const targetBox = finding.targetBox;
  const hasBox = isValidBox(targetBox);

  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
  }, [url]);

  const { vw, vh } = resolveBoxViewport(targetBox, naturalSize);
  const styles = resolveHighlightStyles(finding.severity || finding.type);

  // Compute viewBox for zoomed target vs full page with balanced contextual aspect ratio
  const viewBox = useMemo(() => {
    if (focusMode === "target" && hasBox && targetBox) {
      const targetW = targetBox.width;
      const targetH = targetBox.height;
      // Maintain natural ~16:9 aspect ratio around target element
      const minW = Math.max(480, targetW * 1.5);
      const minH = Math.max(270, targetH * 2.0, minW * (9 / 16));
      const cropW = Math.min(vw, minW);
      const cropH = Math.min(vh, Math.max(minH, cropW * (9 / 16)));
      const centerX = targetBox.x + targetW / 2;
      const centerY = targetBox.y + targetH / 2;
      const cropX = Math.max(0, Math.min(vw - cropW, centerX - cropW / 2));
      const cropY = Math.max(0, Math.min(vh - cropH, centerY - cropH / 2));
      return `${cropX} ${cropY} ${cropW} ${cropH}`;
    }
    return `0 0 ${vw} ${vh}`;
  }, [focusMode, hasBox, targetBox, vw, vh]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Skeleton className="h-[75vh] w-full max-w-5xl rounded-lg" />
      </div>
    );
  }

  if (!url) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center p-8 text-center">
        <div className="rounded-lg border border-border/60 bg-muted/20 p-8 max-w-md space-y-3">
          <p className="text-sm font-medium text-foreground">
            No screenshot captured for Step {finding.stepNumber ?? 1}
          </p>
          <p className="text-xs text-muted-foreground">
            This finding was recorded from an API transition, background event,
            or without browser viewport capture.
          </p>
        </div>
      </div>
    );
  }

  const labelText =
    finding.title ??
    (finding.type === "helps" ? "Strength observed" : "Problem area");

  // Leader line & badge positioning
  const flipBelow = hasBox && targetBox ? targetBox.y < 40 : false;
  const labelY =
    hasBox && targetBox
      ? flipBelow
        ? targetBox.y + targetBox.height + 24
        : Math.max(20, targetBox.y - 12)
      : 20;
  const labelX =
    hasBox && targetBox
      ? Math.max(12, Math.min(targetBox.x + 4, vw - 280))
      : 12;

  return (
    <div
      className={cn(
        "relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-muted/15 p-4 sm:p-6",
        className,
      )}
    >
      {/* Top Floating Canvas Toolbar */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        {hasBox && (
          <SimpleTooltip
            content={
              <div className="flex items-center gap-1.5 text-xs">
                <span>
                  {focusMode === "target"
                    ? "View full page"
                    : "Zoom into element"}
                </span>
                <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                  Z
                </kbd>
              </div>
            }
          >
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFocusMode}
              className="h-8 gap-1.5 rounded-md border-border/80 bg-background/90 text-xs shadow-xs backdrop-blur-xs transition-colors hover:bg-background"
              aria-label={
                focusMode === "target" ? "View full page" : "Zoom into element"
              }
            >
              {focusMode === "target" ? (
                <>
                  <ZoomOut className="size-3.5" />
                  <span className="hidden sm:inline">Full Page</span>
                </>
              ) : (
                <>
                  <ZoomIn className="size-3.5" />
                  <span className="hidden sm:inline">Focus Element</span>
                </>
              )}
              <kbd className="hidden sm:inline-block rounded border border-border bg-muted px-1 font-mono text-2xs text-muted-foreground">
                Z
              </kbd>
            </Button>
          </SimpleTooltip>
        )}

        <SimpleTooltip
          content={
            <div className="flex items-center gap-1.5 text-xs">
              <span>
                {showHighlights ? "Hide annotations" : "Show annotations"}
              </span>
              <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                H
              </kbd>
            </div>
          }
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleHighlights}
            className="h-8 gap-1.5 rounded-md border-border/80 bg-background/90 text-xs shadow-xs backdrop-blur-xs transition-colors hover:bg-background"
            aria-label={
              showHighlights ? "Hide annotations" : "Show annotations"
            }
          >
            {showHighlights ? (
              <>
                <Eye className="size-3.5 text-primary" />
                <span>Highlighted</span>
              </>
            ) : (
              <>
                <EyeOff className="size-3.5 text-muted-foreground" />
                <span>Clean</span>
              </>
            )}
            <kbd className="hidden sm:inline-block rounded border border-border bg-muted px-1 font-mono text-2xs text-muted-foreground">
              H
            </kbd>
          </Button>
        </SimpleTooltip>
      </div>

      {/* SVG Canvas Area */}
      <div className="relative flex h-full w-full max-h-[82vh] items-center justify-center overflow-hidden rounded-lg shadow-sm">
        <svg
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          className="block max-h-full max-w-full select-none"
          role="img"
          aria-label={finding.title || `Step ${finding.stepNumber} screenshot`}
        >
          {/* Base Raster Image */}
          <image href={url} width={vw} height={vh} preserveAspectRatio="none" />

          {/* Target Highlight & Annotation Callout */}
          {showHighlights && hasBox && targetBox && (
            <g
              onClick={() => onSelectHighlight?.(finding.id)}
              className="cursor-pointer"
            >
              {/* High-contrast outer boundary */}
              <rect
                x={targetBox.x}
                y={targetBox.y}
                width={targetBox.width}
                height={targetBox.height}
                rx={4}
                fill="none"
                stroke="rgba(0,0,0,0.85)"
                strokeWidth={4}
                style={{ vectorEffect: "non-scaling-stroke" }}
              />

              {/* Themed colored highlight stroke & tint */}
              <rect
                x={targetBox.x}
                y={targetBox.y}
                width={targetBox.width}
                height={targetBox.height}
                rx={4}
                fill={styles.fill}
                stroke={styles.stroke}
                strokeWidth={2.5}
                style={{ vectorEffect: "non-scaling-stroke" }}
                className={cn(
                  finding.type === "helps"
                    ? "drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                    : "drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]",
                )}
              />

              {/* Anchored Callout Badge */}
              <g transform={`translate(${labelX}, ${labelY})`}>
                <rect
                  x={-2}
                  y={-16}
                  width={Math.min(320, Math.max(48, labelText.length * 8 + 20))}
                  height={22}
                  rx={4}
                  fill={styles.badgeBg}
                  fillOpacity={0.94}
                  stroke="rgba(255,255,255,0.25)"
                  strokeWidth={1}
                  style={{ vectorEffect: "non-scaling-stroke" }}
                />
                <text
                  x={8}
                  y={-1}
                  fill={styles.badgeText}
                  fontSize={12}
                  fontFamily="Inter, system-ui, sans-serif"
                  fontWeight={600}
                  className="select-none"
                >
                  {labelText.length > 36
                    ? `${labelText.slice(0, 34)}…`
                    : labelText}
                </text>
              </g>
            </g>
          )}
        </svg>
      </div>

      {/* Captured URL subtitle */}
      {finding.pageUrl && (
        <div className="mt-2 flex max-w-xl items-center gap-1.5 truncate text-center text-xs text-muted-foreground">
          <span className="font-mono truncate">{finding.pageUrl}</span>
        </div>
      )}
    </div>
  );
}
